/**
 * Attribution Tracker V2
 * ------------------------------------------------------------------
 * Captures the FULL ordered journey of marketing touchpoints for a
 * visitor (not just first/last) and syncs a compact record to Shopify
 * cart attributes so it lands on the order as note attributes.
 *
 * What counts as a touchpoint:
 *   1. Landing with any UTM parameter or ad-platform click ID
 *   2. Landing from an external referrer (classified as organic
 *      search / social / referral)
 *   3. A brand-new session with no referrer (classified as direct)
 *
 * Internal navigation never creates touches. Consecutive duplicate
 * touches within 30 minutes are deduped.
 *
 * Order attributes written:
 *   attribution_journey  - compact JSON array of touch objects
 *   first_touch          - "source / medium / campaign" (human readable)
 *   last_touch           - "source / medium / campaign" (human readable)
 *   touch_count          - true number of touches (journey may be trimmed)
 *   first_touch_at       - ISO date of first touch
 *   last_gclid / last_fbclid / last_ttclid / ... - most recent click IDs
 *
 * Install: theme.liquid, just before </head>, inside a <script> tag,
 * replacing the V1 snippet. V1 cookies are migrated automatically.
 */
(function () {
  'use strict';

  // --- CONFIGURATION -----------------------------------------------
  var CONFIG = {
    utmParams: ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'],
    clickIdParams: ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid'],
    maxTouches: 20,          // hard cap on touches kept in the journey
    journeyDays: 90,         // touches older than this are pruned
    dedupeMinutes: 30,       // identical consecutive touches within this window are skipped
    maxJourneyChars: 1800,   // keep the cart attribute comfortably small
    storageKey: 'attr_journey_v2',
    syncKey: 'attr_last_sync_v2',
    sessionKey: 'attr_session_seen',

    /**
     * CONSENT GATE - wire this to your CMP before EU/UK launch.
     * Examples:
     *   OneTrust:  return (window.OnetrustActiveGroups || '').indexOf('C0002') !== -1;
     *   Cookiebot: return !!(window.Cookiebot && window.Cookiebot.consent.statistics);
     * Defaults to true (no CMP installed).
     */
    hasConsent: function () { return true; }
  };

  if (!CONFIG.hasConsent()) return;

  // --- SAFE STORAGE (degrades to in-memory if localStorage blocked) --
  var memStore = {};
  function lsGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return memStore[key] || null; }
  }
  function lsSet(key, val) {
    try { window.localStorage.setItem(key, val); } catch (e) { memStore[key] = val; }
  }
  function ssGet(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function ssSet(key, val) {
    try { window.sessionStorage.setItem(key, val); } catch (e) { /* noop */ }
  }

  // --- V1 COOKIE MIGRATION -------------------------------------------
  function getCookie(name) {
    var nameEQ = name + '=';
    var ca = document.cookie.split(';');
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i].trim();
      if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length));
    }
    return null;
  }

  function migrateV1(journey) {
    if (journey.length > 0) return journey; // already on V2
    var firstSource = getCookie('first_utm_source');
    var lastSource = getCookie('last_utm_source');
    if (!firstSource && !lastSource) return journey;

    var utmDate = getCookie('utm_date');
    var firstTs = utmDate ? new Date(utmDate + 'T00:00:00Z').getTime() : Date.now();

    if (firstSource) {
      journey.push({
        s: firstSource,
        m: getCookie('first_utm_medium') || '',
        c: getCookie('first_utm_campaign') || '',
        ts: firstTs,
        mig: 1
      });
    }
    var sameAsFirst = lastSource === firstSource &&
      getCookie('last_utm_medium') === getCookie('first_utm_medium') &&
      getCookie('last_utm_campaign') === getCookie('first_utm_campaign');
    if (lastSource && !sameAsFirst) {
      journey.push({
        s: lastSource,
        m: getCookie('last_utm_medium') || '',
        c: getCookie('last_utm_campaign') || '',
        ts: Date.now(),
        mig: 1
      });
    }
    return journey;
  }

  // --- REFERRER CLASSIFICATION ----------------------------------------
  var SEARCH_ENGINES = ['google.', 'bing.', 'duckduckgo.', 'yahoo.', 'ecosia.', 'search.brave.', 'startpage.'];
  var SOCIAL_SITES = {
    'facebook.com': 'facebook', 'instagram.com': 'instagram', 'l.instagram.com': 'instagram',
    'lm.facebook.com': 'facebook', 'm.facebook.com': 'facebook', 't.co': 'twitter',
    'twitter.com': 'twitter', 'x.com': 'twitter', 'tiktok.com': 'tiktok',
    'reddit.com': 'reddit', 'out.reddit.com': 'reddit', 'youtube.com': 'youtube',
    'linkedin.com': 'linkedin', 'pinterest.com': 'pinterest', 'snapchat.com': 'snapchat'
  };

  function refHost() {
    try {
      if (!document.referrer) return null;
      return new URL(document.referrer).hostname.toLowerCase();
    } catch (e) { return null; }
  }

  // Domains that are plumbing, not marketing: Shopify auth/infrastructure,
  // app-served form redirects, and payment-provider returns. Arrivals from
  // these are treated like internal navigation so logins, app round-trips,
  // and payment returns never create fake "referral" touchpoints.
  // NOTE: shop.app is deliberately NOT ignored - Shop app browsing is
  // genuine discovery and counts as a referral channel.
  var IGNORED_REFERRERS = [
    'shopify.com', 'shopifyapps.com', 'hulkapps.com',
    'paypal.com', 'klarna.com', 'afterpay.com', 'affirm.com', 'sezzle.com'
  ];

  function isInternal(host) {
    if (!host) return false;
    var here = window.location.hostname.toLowerCase();
    if (host === here || host.slice(-(here.length + 1)) === '.' + here) return true;
    for (var i = 0; i < IGNORED_REFERRERS.length; i++) {
      var d = IGNORED_REFERRERS[i];
      if (host === d || host.slice(-(d.length + 1)) === '.' + d) return true;
    }
    return false;
  }

  function classifyReferrer(host) {
    var bare = host.replace(/^www\./, '');
    for (var i = 0; i < SEARCH_ENGINES.length; i++) {
      if (bare.indexOf(SEARCH_ENGINES[i]) === 0 || bare.indexOf('.' + SEARCH_ENGINES[i]) !== -1) {
        return { s: bare.split('.')[0], m: 'organic' };
      }
    }
    for (var key in SOCIAL_SITES) {
      if (bare === key || bare.slice(-(key.length + 1)) === '.' + key) {
        return { s: SOCIAL_SITES[key], m: 'social' };
      }
    }
    return { s: bare, m: 'referral' };
  }

  // --- BUILD THE CURRENT TOUCH (if this pageview qualifies) -----------
  var urlParams = new URLSearchParams(window.location.search);

  function buildTouch() {
    var touch = null;

    // App-proxy pages (subscription portals, app-served pages) are
    // account management, not marketing landings - never record a
    // touch here, even if the URL carries UTMs (e.g. transactional
    // "manage your subscription" emails).
    var path = window.location.pathname;
    if (path.indexOf('/tools/') === 0 || path.indexOf('/apps/') === 0 || path.indexOf('/a/') === 0) {
      return null;
    }

    // 1. UTM-tagged landing
    var source = urlParams.get('utm_source');
    var medium = urlParams.get('utm_medium');
    var campaign = urlParams.get('utm_campaign');
    if (source || medium || campaign) {
      touch = {
        s: (source || '').toLowerCase(),
        m: (medium || '').toLowerCase(),
        c: campaign || ''
      };
      var term = urlParams.get('utm_term');
      var content = urlParams.get('utm_content');
      if (term) touch.tm = term;
      if (content) touch.ct = content;
    }

    // 2. Click IDs (may accompany UTMs or arrive alone)
    var clickIdType = null;
    for (var i = 0; i < CONFIG.clickIdParams.length; i++) {
      var p = CONFIG.clickIdParams[i];
      var v = urlParams.get(p);
      if (v) {
        clickIdType = clickIdType || p;
        lsSet('attr_last_' + p, v); // full value stored separately, synced below
      }
    }
    if (clickIdType && !touch) {
      // Click ID but no UTMs: infer the platform (an agency tagging miss)
      var inferred = { gclid: ['google', 'cpc'], gbraid: ['google', 'cpc'], wbraid: ['google', 'cpc'],
                       fbclid: ['facebook', 'paid'], ttclid: ['tiktok', 'paid'], msclkid: ['bing', 'cpc'] }[clickIdType];
      touch = { s: inferred[0], m: inferred[1], c: '(untagged - ' + clickIdType + ')' };
    }
    if (clickIdType && touch) touch.cidt = clickIdType;
    if (touch) return finalize(touch);

    // 3. External referrer
    var host = refHost();
    if (host && !isInternal(host)) {
      return finalize(classifyReferrer(host));
    }

    // 4. Direct: no referrer AND a brand-new session
    if (!host && !ssGet(CONFIG.sessionKey)) {
      return finalize({ s: '(direct)', m: '(none)' });
    }

    return null; // internal navigation - no touch
  }

  function finalize(touch) {
    touch.ts = Date.now();
    touch.lp = window.location.pathname.slice(0, 80);
    return touch;
  }

  // --- JOURNEY MAINTENANCE --------------------------------------------
  function loadJourney() {
    try {
      var raw = lsGet(CONFIG.storageKey);
      var journey = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(journey)) journey = [];
      // prune expired touches
      var cutoff = Date.now() - CONFIG.journeyDays * 24 * 60 * 60 * 1000;
      return journey.filter(function (t) { return t && t.ts >= cutoff; });
    } catch (e) { return []; }
  }

  function sameChannel(a, b) {
    return a && b && a.s === b.s && a.m === b.m && (a.c || '') === (b.c || '');
  }

  function appendTouch(journey, touch) {
    var prev = journey[journey.length - 1];
    var withinDedupe = prev && (touch.ts - prev.ts) < CONFIG.dedupeMinutes * 60 * 1000;
    if (prev && sameChannel(prev, touch) && withinDedupe) return journey;
    journey.push(touch);
    if (journey.length > CONFIG.maxTouches) {
      // keep first 3 (discovery) + most recent (conversion path)
      journey = journey.slice(0, 3).concat(journey.slice(-(CONFIG.maxTouches - 3)));
    }
    return journey;
  }

  // --- RUN CAPTURE ------------------------------------------------------
  var journey = migrateV1(loadJourney());
  var trueCount = parseInt(lsGet('attr_touch_count') || '0', 10) || journey.length;

  var newTouch = buildTouch();
  if (newTouch) {
    var before = journey.length;
    journey = appendTouch(journey, newTouch);
    if (journey.length !== before || before === 0) trueCount += 1;
  }
  if (trueCount < journey.length) trueCount = journey.length;

  lsSet(CONFIG.storageKey, JSON.stringify(journey));
  lsSet('attr_touch_count', String(trueCount));
  ssSet(CONFIG.sessionKey, '1');

  // --- BUILD CART ATTRIBUTES --------------------------------------------
  function label(t) {
    return [t.s || '?', t.m || '?', t.c || ''].join(' / ').replace(/ \/ $/, '');
  }

  function serializeJourney(list) {
    var json = JSON.stringify(list);
    // Trim middle touches if too large for a tidy cart attribute
    var work = list.slice();
    while (json.length > CONFIG.maxJourneyChars && work.length > 4) {
      work.splice(Math.floor(work.length / 2), 1);
      json = JSON.stringify(work);
    }
    return json;
  }

  function buildAttributes() {
    if (journey.length === 0) return null;
    var attrs = {
      attribution_journey: serializeJourney(journey),
      first_touch: label(journey[0]),
      last_touch: label(journey[journey.length - 1]),
      touch_count: String(trueCount),
      first_touch_at: new Date(journey[0].ts).toISOString().split('T')[0]
    };
    CONFIG.clickIdParams.forEach(function (p) {
      var v = lsGet('attr_last_' + p);
      if (v) attrs['last_' + p] = v;
    });
    return attrs;
  }

  // --- SYNC TO CART (only when something changed) -------------------------
  function syncToCart(force) {
    var attrs = buildAttributes();
    if (!attrs) return;
    var payload = JSON.stringify({ attributes: attrs });
    if (!force && lsGet(CONFIG.syncKey) === payload) return; // nothing new

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload
    }).then(function (res) {
      if (res.ok) lsSet(CONFIG.syncKey, payload);
    }).catch(function (err) {
      console.warn('[Attribution] Cart sync failed', err);
    });
  }

  syncToCart(false);

  // Re-sync after add-to-cart: a fresh cart is created without our
  // attributes if the previous one expired, so force a sync then.
  var _fetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var promise = _fetch.apply(this, arguments);
    if (url.indexOf('/cart/add') !== -1) {
      promise.then(function () { setTimeout(function () { syncToCart(true); }, 500); });
    }
    return promise;
  };

  // Manual hook for debugging: window.__attrDebug()
  window.__attrDebug = function () {
    return { journey: journey, touchCount: trueCount, attributes: buildAttributes() };
  };
})();